import { GraphQLRequester } from '../lib/graphql-requester';
import { getSiteIdForLocale } from './getSiteId';
import type { PreviewMode } from '@/contexts/SiteContext';
import type { getSdk as GetSdkType } from '@/lib/graphql-sdk';

// Enable debug logs only in development
const DEBUG = false;

// Debug logging helper
function logDebug(message: string, data?: any) {
    if (DEBUG) {
        console.log(`[Craft SDK] ${message}`, data);
    }
}

// Create a fresh GraphQL requester for each request (no singleton)
function createRequester(previewMode?: PreviewMode): GraphQLRequester {
    logDebug("Creating fresh GraphQLRequester", previewMode ? { previewMode } : undefined);
    return new GraphQLRequester(
        undefined,
        undefined,
        previewMode || { mode: "none", code: null, token: null }
    );
}

// Define a type that includes both our core utilities and all SDK methods
type SDK = ReturnType<typeof GetSdkType>;

export interface CraftSDK extends Record<keyof SDK, (variables?: any) => Promise<{ data: any }>> {
    // Core utility methods
    getSdk: () => SDK;
    getClient: () => any;
}

// Create a dynamic proxy for the SDK that handles all method calls
export const craftSdk = new Proxy({} as CraftSDK, {
    get(target, prop: string) {
        // For SDK methods, create a dynamic function that executes them
        return (variables?: any) => {
            const previewMode = variables?._previewMode;
            const requester = createRequester(previewMode);
            const sdk = requester.getSdk();
            
            // Check if method exists in SDK
            if (prop in sdk) {
                logDebug(`Executing SDK method: ${prop}`, { 
                    hasPreviewMode: !!previewMode,
                    variables: variables ? Object.keys(variables) : []
                });
                return (sdk as any)[prop](variables);
            }
            
            // Core utilities fallback
            if (prop === 'getSdk') {
                return () => sdk;
            }
            
            if (prop === 'getClient') {
                return () => requester.getClient();
            }
            
            // If method doesn't exist, throw a descriptive error
            throw new Error(`Method "${prop}" not found in SDK. Available methods: ${Object.keys(sdk).join(', ')}`);
        };
    }
});

// Helper function to create variables for Craft CMS queries
export const craftVars = (locale: string, variables: Record<string, any> = {}, previewMode?: PreviewMode) => {
    return {
        ...variables,
        siteId: [getSiteIdForLocale(locale)],
        _previewMode: previewMode // Pass preview mode as a private variable
    };
};